import { useEffect, useMemo, useRef } from 'react'
import type { Composition } from '@shared/types'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import { findSegmentAt, getSegmentTimeline, getTotalDurationSec } from '../../../lib/timelineMath'

/**
 * Drives the double-buffered <video> preview: two <video> elements, one
 * "active" (playing, visible) and one "standby" (preloaded with the next
 * Segment's proxy so the cut is instant). Advances store.playback.currentSec
 * via requestAnimationFrame while isPlaying, wrapping at the timeline end.
 */
export function usePreviewPlayback(composition: Composition | null) {
  const videoRefs = [useRef<HTMLVideoElement>(null), useRef<HTMLVideoElement>(null)]
  // Background "blurred + enlarged" copy of the same source, shown behind
  // the foreground video only for `fit: 'contain'` Segments (PreviewPanel
  // decides visibility; this hook just keeps both refs' src/currentTime/
  // playbackRate/play-state in lockstep with the foreground so whichever
  // one PreviewPanel makes visible is already at the right frame).
  const bgVideoRefs = [useRef<HTMLVideoElement>(null), useRef<HTMLVideoElement>(null)]
  const activeSlotRef = useRef<0 | 1>(0)
  const loadedSegIdRef = useRef<[string | null, string | null]>([null, null])
  const loadedBgSegIdRef = useRef<[string | null, string | null]>([null, null])
  const rafRef = useRef<number | null>(null)
  const lastTsRef = useRef<number | null>(null)

  const { currentSec, isPlaying } = useEditorStore((s) => s.playback)
  const setPlayhead = useEditorStore((s) => s.setPlayhead)
  const setPlaying = useEditorStore((s) => s.setPlaying)
  const assets = useEditorStore((s) => s.assets)

  const timeline = useMemo(
    () => (composition ? getSegmentTimeline(composition.tracks.video) : []),
    [composition],
  )
  const totalSec = useMemo(() => (composition ? getTotalDurationSec(composition) : 0), [composition])

  function loadSlot(slot: 0 | 1, timedSeg: (typeof timeline)[number] | undefined) {
    const video = videoRefs[slot].current
    if (!video) return
    if (!timedSeg) {
      loadedSegIdRef.current[slot] = null
      return
    }
    const asset = assets[timedSeg.segment.assetId]
    const src = api.fileUrl(asset?.proxyPath ?? asset?.originalPath)
    if (loadedSegIdRef.current[slot] !== timedSeg.segment.id) {
      if (src && video.src !== src) video.src = src
      video.currentTime = timedSeg.segment.inSec
      video.playbackRate = timedSeg.segment.speed
      loadedSegIdRef.current[slot] = timedSeg.segment.id
    }
  }

  // Same as loadSlot, for the blurred-background copy — only bothers
  // loading a src when the Segment is `fit: 'contain'` (the only case
  // PreviewPanel makes this layer visible), so a 'cover' Segment doesn't
  // pay for a second decode of the same file it's not going to show.
  function loadBgSlot(slot: 0 | 1, timedSeg: (typeof timeline)[number] | undefined) {
    const video = bgVideoRefs[slot].current
    if (!video) return
    if (!timedSeg || timedSeg.segment.fit !== 'contain') {
      loadedBgSegIdRef.current[slot] = null
      return
    }
    const asset = assets[timedSeg.segment.assetId]
    const src = api.fileUrl(asset?.proxyPath ?? asset?.originalPath)
    if (loadedBgSegIdRef.current[slot] !== timedSeg.segment.id) {
      if (src && video.src !== src) video.src = src
      video.currentTime = timedSeg.segment.inSec
      video.playbackRate = timedSeg.segment.speed
      loadedBgSegIdRef.current[slot] = timedSeg.segment.id
    }
  }

  // Keep the active + standby slots in sync with the current playhead position.
  useEffect(() => {
    const current = findSegmentAt(timeline, currentSec)
    if (!current) return
    const standbySlot: 0 | 1 = activeSlotRef.current === 0 ? 1 : 0

    // If the segment already loaded in the "next" slot is the one we now
    // need, just flip which slot is active (seamless — no reload).
    if (loadedSegIdRef.current[standbySlot] === current.segment.id) {
      activeSlotRef.current = standbySlot
    } else if (loadedSegIdRef.current[activeSlotRef.current] !== current.segment.id) {
      loadSlot(activeSlotRef.current, current)
    }
    // Bg slot's "loaded id" tracks fit too (loadBgSlot clears it for
    // non-'contain' segments), so the same flip-vs-reload choice made for
    // the foreground pair is redone independently for the bg pair — a
    // Segment can flip fg without needing to (re)load bg at all if it was
    // never 'contain' to begin with.
    if (loadedBgSegIdRef.current[standbySlot] === current.segment.id) {
      // already the flipped-to slot; nothing to load
    } else if (loadedBgSegIdRef.current[activeSlotRef.current] !== current.segment.id) {
      loadBgSlot(activeSlotRef.current, current)
    }

    const next = timeline[current.index + 1]
    const standbyNow: 0 | 1 = activeSlotRef.current === 0 ? 1 : 0
    loadSlot(standbyNow, next)
    loadBgSlot(standbyNow, next)

    const activeVideo = videoRefs[activeSlotRef.current].current
    const activeBgVideo = bgVideoRefs[activeSlotRef.current].current
    if (activeVideo) {
      // P10-2 担当B §3 item 5: `playbackRate` was already being set to the
      // Segment's own speed for the video track (unrelated to sourceAudio) —
      // noting here since sourceAudio's own preview approximation rides on
      // it too: when sourceAudio is enabled, this SAME `playbackRate` also
      // speeds up/slows down whatever audio the (unmuted) active <video>
      // itself carries, which shifts its pitch (the browser's native
      // <video> playbackRate has no independent time-stretch — unlike the
      // real render's `atempo` filter, which changes duration WITHOUT
      // changing pitch). Approximation, not a pixel/sample-identical
      // preview — same spirit as the color-grade CSS layer elsewhere in
      // this file's sibling PreviewPanel.tsx.
      activeVideo.playbackRate = current.segment.speed
      const expected = current.segment.inSec + (currentSec - current.startSec) * current.segment.speed
      if (Math.abs(activeVideo.currentTime - expected) > 0.2) {
        activeVideo.currentTime = expected
      }
      // P10-2 担当B §3 item 5: React has no reliable declarative `volume`
      // prop for <video> (a documented gap — same reason usePreviewAudio.ts's
      // <audio> sync effect sets `el.volume` imperatively instead of via
      // JSX) — set it here every time this effect runs, so a live gainDb
      // slider drag takes effect immediately. `muted` itself is NOT set
      // here — PreviewPanel.tsx's JSX `muted={...}` prop already owns that
      // (React DOES support toggling `muted` declaratively), so setting it
      // again here would be redundant, not wrong — left out to keep this
      // effect's responsibility to timing/rate/volume only.
      if (composition?.sourceAudio?.enabled) {
        activeVideo.volume = Math.min(1, Math.pow(10, composition.sourceAudio.gainDb / 20))
      }
      if (isPlaying) activeVideo.play().catch(() => {})
      else activeVideo.pause()
    }
    if (activeBgVideo && current.segment.fit === 'contain') {
      activeBgVideo.playbackRate = current.segment.speed
      const expected = current.segment.inSec + (currentSec - current.startSec) * current.segment.speed
      if (Math.abs(activeBgVideo.currentTime - expected) > 0.2) {
        activeBgVideo.currentTime = expected
      }
      if (isPlaying) activeBgVideo.play().catch(() => {})
      else activeBgVideo.pause()
    } else {
      activeBgVideo?.pause()
    }
    const standbyVideo = videoRefs[standbyNow].current
    standbyVideo?.pause()
    bgVideoRefs[standbyNow].current?.pause()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeline,
    currentSec,
    isPlaying,
    assets,
    // P10-2 担当B: re-run (and re-apply activeVideo.volume above) the moment
    // sourceAudio.enabled/gainDb changes — otherwise dragging the gain
    // slider while paused would only take effect on the NEXT currentSec
    // change (a seek/play), not immediately.
    composition?.sourceAudio?.enabled,
    composition?.sourceAudio?.gainDb,
  ])

  // RAF loop: advance currentSec in real time while playing.
  useEffect(() => {
    if (!isPlaying) {
      lastTsRef.current = null
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }
    const tick = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts
      const dt = (ts - lastTsRef.current) / 1000
      lastTsRef.current = ts
      const store = useEditorStore.getState()
      const next = store.playback.currentSec + dt
      if (totalSec > 0 && next >= totalSec) {
        setPlayhead(0)
        setPlaying(false)
        return
      }
      setPlayhead(next)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, totalSec])

  const activeSegment = findSegmentAt(timeline, currentSec)?.segment ?? null

  return { videoRefs, bgVideoRefs, activeSlot: activeSlotRef.current, activeSegment, totalSec, timeline }
}
